import { useNavigate } from 'react-router-dom';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useCreateChain } from '@/hooks/queries/useChains';
import { formatCurrency, strategyDisplayNames } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { parseTosOrder, isTosOrderString, generateTosOrderString } from '@/lib/parseTosOrder';
import { quotesApi } from '@/lib/api/client';
import toast from 'react-hot-toast';
import type { StrategyType, CreateChainRequest, CreateLegRequest } from '@steward/shared';

type LegTemplate = {
  optionType: 'call' | 'put';
  action: 'BTO' | 'STO';
  strikePrice: number;
  expirationDate: string;
  quantity: number;
  pricePerContract: number;
};

const strategyLegTemplates: Record<StrategyType, LegTemplate[]> = {
  iron_condor: [
    { optionType: 'put', action: 'BTO', strikePrice: 0, expirationDate: '', quantity: 1, pricePerContract: 0 },
    { optionType: 'put', action: 'STO', strikePrice: 0, expirationDate: '', quantity: 1, pricePerContract: 0 },
    { optionType: 'call', action: 'STO', strikePrice: 0, expirationDate: '', quantity: 1, pricePerContract: 0 },
    { optionType: 'call', action: 'BTO', strikePrice: 0, expirationDate: '', quantity: 1, pricePerContract: 0 },
  ],
  iron_butterfly: [
    { optionType: 'put', action: 'BTO', strikePrice: 0, expirationDate: '', quantity: 1, pricePerContract: 0 },
    { optionType: 'put', action: 'STO', strikePrice: 0, expirationDate: '', quantity: 1, pricePerContract: 0 },
    { optionType: 'call', action: 'STO', strikePrice: 0, expirationDate: '', quantity: 1, pricePerContract: 0 },
    { optionType: 'call', action: 'BTO', strikePrice: 0, expirationDate: '', quantity: 1, pricePerContract: 0 },
  ],
  cash_secured_put: [
    { optionType: 'put', action: 'STO', strikePrice: 0, expirationDate: '', quantity: 1, pricePerContract: 0 },
  ],
  covered_call: [
    { optionType: 'call', action: 'STO', strikePrice: 0, expirationDate: '', quantity: 1, pricePerContract: 0 },
  ],
  vertical_spread: [
    { optionType: 'put', action: 'STO', strikePrice: 0, expirationDate: '', quantity: 1, pricePerContract: 0 },
    { optionType: 'put', action: 'BTO', strikePrice: 0, expirationDate: '', quantity: 1, pricePerContract: 0 },
  ],
  single_call: [
    { optionType: 'call', action: 'BTO', strikePrice: 0, expirationDate: '', quantity: 1, pricePerContract: 0 },
  ],
  single_put: [
    { optionType: 'put', action: 'BTO', strikePrice: 0, expirationDate: '', quantity: 1, pricePerContract: 0 },
  ],
  straddle: [
    { optionType: 'put', action: 'STO', strikePrice: 0, expirationDate: '', quantity: 1, pricePerContract: 0 },
    { optionType: 'call', action: 'STO', strikePrice: 0, expirationDate: '', quantity: 1, pricePerContract: 0 },
  ],
  strangle: [
    { optionType: 'put', action: 'STO', strikePrice: 0, expirationDate: '', quantity: 1, pricePerContract: 0 },
    { optionType: 'call', action: 'STO', strikePrice: 0, expirationDate: '', quantity: 1, pricePerContract: 0 },
  ],
  calendar_spread: [
    { optionType: 'call', action: 'STO', strikePrice: 0, expirationDate: '', quantity: 1, pricePerContract: 0 },
    { optionType: 'call', action: 'BTO', strikePrice: 0, expirationDate: '', quantity: 1, pricePerContract: 0 },
  ],
  diagonal_spread: [
    { optionType: 'call', action: 'STO', strikePrice: 0, expirationDate: '', quantity: 1, pricePerContract: 0 },
    { optionType: 'call', action: 'BTO', strikePrice: 0, expirationDate: '', quantity: 1, pricePerContract: 0 },
  ],
  custom: [
    { optionType: 'put', action: 'STO', strikePrice: 0, expirationDate: '', quantity: 1, pricePerContract: 0 },
  ],
};

interface FormValues {
  underlyingSymbol: string;
  strategyType: StrategyType;
  notes: string;
  tags: string;
  legs: Array<{
    optionType: 'call' | 'put';
    strikePrice: number;
    expirationDate: string;
    action: 'BTO' | 'STO';
    quantity: number;
    pricePerContract: number;
  }>;
}

const strategyOptions = Object.entries(strategyDisplayNames).map(([value, label]) => ({
  value,
  label,
}));

const actionOptions = [
  { value: 'STO', label: 'Sell to Open (STO)' },
  { value: 'BTO', label: 'Buy to Open (BTO)' },
];

const typeOptions = [
  { value: 'call', label: 'Call' },
  { value: 'put', label: 'Put' },
];

export function TradeEntryForm() {
  const navigate = useNavigate();
  const createChain = useCreateChain();

  const { register, control, handleSubmit, watch, setValue, getValues, formState: { errors } } = useForm<FormValues>({
    defaultValues: {
      underlyingSymbol: '',
      strategyType: 'iron_condor',
      notes: '',
      tags: '',
      legs: strategyLegTemplates.iron_condor,
    },
  });

  // Track whether quantity/expiration are linked to first leg
  const [quantityLinked, setQuantityLinked] = useState(true);
  const [expirationLinked, setExpirationLinked] = useState(true);

  // TOS order string input
  const [tosOrderString, setTosOrderString] = useState('');
  const [tosParseError, setTosParseError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Price fetching
  const [fetchingPrices, setFetchingPrices] = useState(false);
  const [underlyingPrice, setUnderlyingPrice] = useState<number | null>(null);
  const [legGreeks, setLegGreeks] = useState<Map<string, { delta: number; theta: number; iv: number }>>(new Map());

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'legs',
  });

  const watchedLegs = watch('legs');
  const watchedSymbol = watch('underlyingSymbol');
  const watchedStrategy = watch('strategyType');

  // Update legs when strategy changes
  useEffect(() => {
    const template = strategyLegTemplates[watchedStrategy];
    if (template) {
      replace(template);
      // Reset linked state when strategy changes
      setQuantityLinked(true);
      setExpirationLinked(true);
    }
  }, [watchedStrategy, replace]);

  // Propagate quantity change - if leg 0 and linked, update all other legs
  const handleQuantityChange = (index: number, value: number) => {
    setValue(`legs.${index}.quantity`, value);
    if (index === 0 && quantityLinked) {
      const legs = getValues('legs');
      for (let i = 1; i < legs.length; i++) {
        setValue(`legs.${i}.quantity`, value);
      }
    } else if (index > 0) {
      setQuantityLinked(false);
    }
  };

  // Propagate expiration change - if leg 0 and linked, update all other legs
  const handleExpirationChange = (index: number, value: string) => {
    setValue(`legs.${index}.expirationDate`, value);
    if (index === 0 && expirationLinked && value) {
      const legs = getValues('legs');
      for (let i = 1; i < legs.length; i++) {
        setValue(`legs.${i}.expirationDate`, value);
      }
    } else if (index > 0) {
      setExpirationLinked(false);
    }
  };

  // Handle TOS order string paste/change
  const handleTosOrderChange = (value: string) => {
    setTosOrderString(value);
    setTosParseError(null);

    if (!value.trim()) {
      return;
    }

    // Only try to parse if it looks like a TOS order string
    if (!isTosOrderString(value)) {
      return;
    }

    const parsed = parseTosOrder(value);
    if (!parsed) {
      setTosParseError('Could not parse order string. Check the format.');
      return;
    }

    // Populate the form with parsed data
    setValue('underlyingSymbol', parsed.underlyingSymbol);
    setValue('strategyType', parsed.strategyType);

    // Build legs with expiration date
    const legsWithExpiration = parsed.legs.map(leg => ({
      ...leg,
      expirationDate: parsed.expirationDate,
    }));

    replace(legsWithExpiration);

    // Reset linked state since we're setting all at once
    setQuantityLinked(true);
    setExpirationLinked(true);

    // Clear the input after successful parse
    setTosOrderString('');
  };

  // Fetch live prices from Schwab
  const handleFetchPrices = async () => {
    const symbol = getValues('underlyingSymbol');
    const legs = getValues('legs');

    if (!symbol) {
      toast.error('Enter a symbol first');
      return;
    }

    // Check if legs have valid data
    const validLegs = legs.filter(
      (leg) => leg.strikePrice > 0 && leg.expirationDate
    );

    if (validLegs.length === 0) {
      toast.error('Enter strike prices and expiration dates first');
      return;
    }

    setFetchingPrices(true);
    try {
      const result = await quotesApi.getOptionLegs(
        symbol,
        validLegs.map((leg) => ({
          optionType: leg.optionType,
          strikePrice: leg.strikePrice,
          expirationDate: leg.expirationDate,
        }))
      );

      // Update underlying price
      if (result.underlying) {
        setUnderlyingPrice(result.underlying.price);
      }

      // Update leg prices - use mark price (mid-point) as the default
      let updatedCount = 0;
      const newGreeks = new Map<string, { delta: number; theta: number; iv: number }>();

      for (let i = 0; i < legs.length; i++) {
        const leg = legs[i];
        const quote = result.legs.find(
          (q) =>
            q.optionType === leg.optionType &&
            Math.abs(q.strikePrice - leg.strikePrice) < 0.01 &&
            q.expirationDate === leg.expirationDate
        );

        if (quote && quote.mark !== null) {
          setValue(`legs.${i}.pricePerContract`, quote.mark);
          updatedCount++;

          // Store Greeks for this leg
          const legKey = `${leg.optionType}-${leg.strikePrice}-${leg.expirationDate}`;
          if (quote.delta !== null && quote.theta !== null && quote.impliedVolatility !== null) {
            newGreeks.set(legKey, {
              delta: quote.delta,
              theta: quote.theta,
              iv: quote.impliedVolatility,
            });
          }
        }
      }

      setLegGreeks(newGreeks);

      if (updatedCount > 0) {
        toast.success(`Updated ${updatedCount} leg price${updatedCount > 1 ? 's' : ''}`);
      } else {
        toast.error('No matching quotes found');
      }
    } catch (error) {
      console.error('Failed to fetch prices:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to fetch prices');
    } finally {
      setFetchingPrices(false);
    }
  };

  const calculateNetPremium = () => {
    return watchedLegs.reduce((sum, leg) => {
      const value = (leg.pricePerContract || 0) * (leg.quantity || 0) * 100;
      return sum + (leg.action === 'STO' ? value : -value);
    }, 0);
  };

  const netPremium = calculateNetPremium();

  // Handle leg deletion with confirmation
  const handleRemoveLeg = (index: number) => {
    if (confirm('Are you sure you want to remove this leg?')) {
      remove(index);
      // Clear Greeks for removed leg
      setLegGreeks(new Map(legGreeks));
    }
  };

  // Generate TOS order string from current form state
  const generatedTosString = generateTosOrderString({
    underlyingSymbol: watchedSymbol,
    strategyType: watchedStrategy,
    legs: watchedLegs,
  });

  const onSubmit = async (data: FormValues) => {
    const legs: CreateLegRequest[] = data.legs.map((leg) => ({
      underlyingSymbol: data.underlyingSymbol.toUpperCase(),
      optionType: leg.optionType,
      strikePrice: leg.strikePrice,
      expirationDate: leg.expirationDate,
      action: leg.action,
      quantity: leg.quantity,
      pricePerContract: leg.pricePerContract,
    }));

    const request: CreateChainRequest = {
      underlyingSymbol: data.underlyingSymbol.toUpperCase(),
      strategyType: data.strategyType,
      notes: data.notes || undefined,
      tags: data.tags ? data.tags.split(',').map((t) => t.trim()).filter(Boolean) : undefined,
      legs,
    };

    const result = await createChain.mutateAsync(request);
    navigate(`/chains/${result.chain.id}`);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Quick Entry */}
      <Card>
        <CardHeader>
          <CardTitle>TOS Order String</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Paste to import */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Paste to Import
            </label>
            <input
              type="text"
              value={tosOrderString}
              onChange={(e) => handleTosOrderChange(e.target.value)}
              onPaste={(e) => {
                // Handle paste immediately
                const pastedText = e.clipboardData.getData('text');
                handleTosOrderChange(pastedText);
              }}
              placeholder="SELL -1 IRON CONDOR SBUX 100 30 JAN 26 103/112/92/82 CALL/PUT @2.65 LMT"
              className={cn(
                'block w-full rounded-lg border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 font-mono',
                tosParseError
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:border-brand-500 focus:ring-brand-500'
              )}
            />
            {tosParseError && (
              <p className="text-sm text-red-600">{tosParseError}</p>
            )}
          </div>

          {/* Generated string from form */}
          {generatedTosString && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Generated Order String
              </label>
              <div className="flex items-center gap-2">
                <code className="flex-1 block rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-mono text-gray-800 overflow-x-auto">
                  {generatedTosString}
                </code>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(generatedTosString);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  className={cn(
                    'flex-shrink-0 p-2 rounded-lg border transition-colors',
                    copied
                      ? 'border-green-300 bg-green-50 text-green-600'
                      : 'border-gray-300 hover:bg-gray-50 text-gray-600'
                  )}
                  title="Copy to clipboard"
                >
                  {copied ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500">
                Copy this to paste into ThinkOrSwim
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Basic Info */}
      <Card>
        <CardHeader>
          <CardTitle>Trade Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Underlying Symbol"
              placeholder="SPY, AAPL, etc."
              {...register('underlyingSymbol', { required: 'Symbol is required' })}
              error={errors.underlyingSymbol?.message}
              className="uppercase"
            />
            <Select
              label="Strategy"
              options={strategyOptions}
              {...register('strategyType')}
            />
          </div>
        </CardContent>
      </Card>

      {/* Legs */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-4">
            <CardTitle>Option Legs</CardTitle>
            {underlyingPrice !== null && (
              <span className="text-sm text-gray-500">
                {watchedSymbol.toUpperCase()}: <span className="font-medium text-gray-900">${underlyingPrice.toFixed(2)}</span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleFetchPrices}
              isLoading={fetchingPrices}
              disabled={!watchedSymbol || watchedLegs.length === 0}
            >
              Fetch Prices
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() =>
                append({
                  optionType: 'put',
                  strikePrice: 0,
                  expirationDate: expirationLinked && watchedLegs[0]?.expirationDate ? watchedLegs[0].expirationDate : '',
                  action: 'STO',
                  quantity: quantityLinked && watchedLegs[0]?.quantity ? watchedLegs[0].quantity : 1,
                  pricePerContract: 0,
                })
              }
            >
              Add Leg
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Column headers - show Greeks columns when data is available */}
          {legGreeks.size > 0 ? (
            <div className="hidden sm:grid sm:grid-cols-[80px_60px_80px_100px_50px_80px_60px_60px_60px_32px] gap-2 text-xs font-medium text-gray-500 px-1">
              <span>Action</span>
              <span>Type</span>
              <span>Strike</span>
              <span className="flex items-center gap-1">
                Exp
                {expirationLinked && fields.length > 1 && (
                  <svg className="w-3 h-3 text-brand-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <title>Linked to first leg</title>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                )}
              </span>
              <span className="flex items-center gap-1">
                Qty
                {quantityLinked && fields.length > 1 && (
                  <svg className="w-3 h-3 text-brand-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <title>Linked to first leg</title>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                )}
              </span>
              <span>Price</span>
              <span className="text-right">Delta</span>
              <span className="text-right">Theta</span>
              <span className="text-right">IV</span>
              <span></span>
            </div>
          ) : (
            <div className="hidden sm:grid sm:grid-cols-[100px_70px_90px_120px_70px_90px_32px] gap-2 text-xs font-medium text-gray-500 px-1">
              <span>Action</span>
              <span>Type</span>
              <span>Strike</span>
              <span className="flex items-center gap-1">
                Expiration
                {expirationLinked && fields.length > 1 && (
                  <svg className="w-3 h-3 text-brand-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <title>Linked to first leg</title>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                )}
              </span>
              <span className="flex items-center gap-1">
                Qty
                {quantityLinked && fields.length > 1 && (
                  <svg className="w-3 h-3 text-brand-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <title>Linked to first leg</title>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                )}
              </span>
              <span>Price</span>
              <span></span>
            </div>
          )}

          {fields.map((field, index) => {
            const legKey = `${watchedLegs[index]?.optionType}-${watchedLegs[index]?.strikePrice}-${watchedLegs[index]?.expirationDate}`;
            const greeks = legGreeks.get(legKey);

            return (
            <div
              key={field.id}
              className="p-3 border border-gray-200 rounded-lg"
            >
              {/* Mobile: Show leg number */}
              <div className="sm:hidden flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-500">Leg {index + 1}</span>
                {fields.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveLeg(index)}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Desktop: Single row layout - with Greeks columns when available */}
              <div className={cn(
                'hidden sm:grid gap-2 items-center',
                legGreeks.size > 0
                  ? 'sm:grid-cols-[80px_60px_80px_100px_50px_80px_60px_60px_60px_32px]'
                  : 'sm:grid-cols-[100px_70px_90px_120px_70px_90px_32px]'
              )}>
                <Controller
                  name={`legs.${index}.action`}
                  control={control}
                  render={({ field }) => (
                    <select
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      ref={field.ref}
                      className="block w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    >
                      {actionOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.value}</option>
                      ))}
                    </select>
                  )}
                />
                <Controller
                  name={`legs.${index}.optionType`}
                  control={control}
                  render={({ field }) => (
                    <select
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      ref={field.ref}
                      className="block w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    >
                      {typeOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  )}
                />
                <Controller
                  name={`legs.${index}.strikePrice`}
                  control={control}
                  rules={{ required: 'Required', min: { value: 0.01, message: 'Must be > 0' } }}
                  render={({ field }) => (
                    <input
                      type="number"
                      step="0.5"
                      min={0}
                      placeholder="Strike"
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.valueAsNumber)}
                      onBlur={field.onBlur}
                      ref={field.ref}
                      className="block w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    />
                  )}
                />
                <Controller
                  name={`legs.${index}.expirationDate`}
                  control={control}
                  rules={{ required: 'Required' }}
                  render={({ field }) => (
                    <input
                      type="date"
                      value={field.value}
                      onChange={(e) => handleExpirationChange(index, e.target.value)}
                      onBlur={field.onBlur}
                      ref={field.ref}
                      className="block w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    />
                  )}
                />
                <Controller
                  name={`legs.${index}.quantity`}
                  control={control}
                  rules={{ required: 'Required', min: { value: 1, message: 'Min 1' } }}
                  render={({ field }) => (
                    <input
                      type="number"
                      min={1}
                      value={field.value}
                      onChange={(e) => handleQuantityChange(index, e.target.valueAsNumber)}
                      onBlur={field.onBlur}
                      ref={field.ref}
                      className="block w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    />
                  )}
                />
                <Controller
                  name={`legs.${index}.pricePerContract`}
                  control={control}
                  rules={{ required: 'Required', min: { value: 0, message: 'Min 0' } }}
                  render={({ field }) => (
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      placeholder="Price"
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.valueAsNumber)}
                      onBlur={field.onBlur}
                      ref={field.ref}
                      className="block w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    />
                  )}
                />
                {/* Greeks columns - only shown when data available */}
                {legGreeks.size > 0 && (
                  <>
                    <span className="text-right text-xs font-mono text-gray-600">
                      {greeks ? greeks.delta.toFixed(3) : '-'}
                    </span>
                    <span className="text-right text-xs font-mono text-gray-600">
                      {greeks ? greeks.theta.toFixed(3) : '-'}
                    </span>
                    <span className="text-right text-xs font-mono text-gray-600">
                      {greeks ? `${(greeks.iv * 100).toFixed(1)}%` : '-'}
                    </span>
                  </>
                )}
                {fields.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => handleRemoveLeg(index)}
                    className="text-gray-400 hover:text-red-500 p-1"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                ) : <div />}
              </div>

              {/* Mobile: Stacked layout */}
              <div className="sm:hidden grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Action</label>
                  <Controller
                    name={`legs.${index}.action`}
                    control={control}
                    render={({ field }) => (
                      <select
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        ref={field.ref}
                        className="block w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        {actionOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.value}</option>
                        ))}
                      </select>
                    )}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Type</label>
                  <Controller
                    name={`legs.${index}.optionType`}
                    control={control}
                    render={({ field }) => (
                      <select
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        ref={field.ref}
                        className="block w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        {typeOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    )}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Strike</label>
                  <Controller
                    name={`legs.${index}.strikePrice`}
                    control={control}
                    render={({ field }) => (
                      <input
                        type="number"
                        step="0.5"
                        min={0}
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.valueAsNumber)}
                        onBlur={field.onBlur}
                        ref={field.ref}
                        className="block w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                    )}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Expiration</label>
                  <Controller
                    name={`legs.${index}.expirationDate`}
                    control={control}
                    render={({ field }) => (
                      <input
                        type="date"
                        value={field.value}
                        onChange={(e) => handleExpirationChange(index, e.target.value)}
                        onBlur={field.onBlur}
                        ref={field.ref}
                        className="block w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                    )}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Qty</label>
                  <Controller
                    name={`legs.${index}.quantity`}
                    control={control}
                    render={({ field }) => (
                      <input
                        type="number"
                        min={1}
                        value={field.value}
                        onChange={(e) => handleQuantityChange(index, e.target.valueAsNumber)}
                        onBlur={field.onBlur}
                        ref={field.ref}
                        className="block w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                    )}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Price</label>
                  <Controller
                    name={`legs.${index}.pricePerContract`}
                    control={control}
                    render={({ field }) => (
                      <input
                        type="number"
                        step="0.01"
                        min={0}
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.valueAsNumber)}
                        onBlur={field.onBlur}
                        ref={field.ref}
                        className="block w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                    )}
                  />
                </div>
              </div>

              {/* Greeks display for mobile only (desktop shows in columns) */}
              {greeks && (
                <div className="sm:hidden mt-2 pt-2 border-t border-gray-100 flex items-center gap-4 text-xs text-gray-600">
                  <span>
                    <span className="font-medium">Delta:</span> {greeks.delta.toFixed(3)}
                  </span>
                  <span>
                    <span className="font-medium">Theta:</span> {greeks.theta.toFixed(3)}
                  </span>
                  <span>
                    <span className="font-medium">IV:</span> {(greeks.iv * 100).toFixed(1)}%
                  </span>
                </div>
              )}
            </div>
          )})}

          {/* Premium Summary */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="font-medium text-gray-900">Net Premium</span>
              <span
                className={cn(
                  'text-xl font-bold',
                  netPremium >= 0 ? 'text-green-600' : 'text-red-600'
                )}
              >
                {formatCurrency(netPremium)}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {netPremium >= 0 ? 'Credit received' : 'Debit paid'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Notes & Tags */}
      <Card>
        <CardHeader>
          <CardTitle>Additional Info</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (optional)
            </label>
            <textarea
              {...register('notes')}
              rows={3}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              placeholder="Trade thesis, rationale, etc."
            />
          </div>
          <Input
            label="Tags (optional)"
            placeholder="earnings, wheel, monthly (comma-separated)"
            {...register('tags')}
            hint="Separate multiple tags with commas"
          />
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button type="button" variant="secondary" onClick={() => navigate('/chains')}>
          Cancel
        </Button>
        <Button
          type="submit"
          isLoading={createChain.isPending}
          disabled={!watchedSymbol || watchedLegs.length === 0}
        >
          Create Trade
        </Button>
      </div>
    </form>
  );
}
